import React from 'react';
import { motion } from 'framer-motion';

/**
 * Animated breathing circle component
 * Expands on inhale, contracts on exhale, steady on holds
 */
export default function BreathingCircle({ phase, progress, isActive, lowMotion }) {
  // Calculate circle scale based on phase and progress
  const getCircleScale = () => {
    if (!isActive) return 1;

    switch (phase) {
      case 'inhale':
        return 1 + (progress * 0.8); // Expand from 1 to 1.8
      case 'hold_in':
        return 1.8; // Stay expanded
      case 'exhale':
        return 1.8 - (progress * 0.8); // Contract from 1.8 to 1
      case 'hold_out':
        return 1; // Stay contracted
      default:
        return 1;
    }
  };

  // Get circle color based on phase
  const getCircleColor = () => {
    switch (phase) {
      case 'inhale':
        return 'from-blue-400 to-cyan-500';
      case 'hold_in':
        return 'from-cyan-500 to-teal-500';
      case 'exhale':
        return 'from-pink-400 to-rose-500';
      case 'hold_out':
        return 'from-purple-400 to-indigo-500';
      default:
        return 'from-blue-400 to-cyan-500';
    }
  };

  const scale = getCircleScale();
  const colorClass = getCircleColor();

  return (
    <div className="flex items-center justify-center py-12">
      <motion.div
        animate={{
          scale: lowMotion ? 1 : scale,
          opacity: isActive ? 1 : 0.5
        }}
        transition={{
          duration: lowMotion ? 0 : 0.3,
          ease: 'easeInOut'
        }}
        className="relative"
      >
        {/* Outer glow ring */}
        <div className={`absolute inset-0 bg-gradient-to-r ${colorClass} rounded-full blur-3xl opacity-30`} />
        
        {/* Main circle */}
        <div className={`relative w-64 h-64 bg-gradient-to-r ${colorClass} rounded-full shadow-2xl flex items-center justify-center`}>
          {/* Inner circle */}
          <div className="w-48 h-48 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center">
            {/* Center dot */}
            <div className="w-4 h-4 bg-white rounded-full shadow-lg" />
          </div>
        </div>

        {/* Progress ring */}
        {isActive && !lowMotion && (
          <svg className="absolute inset-0 w-full h-full -rotate-90">
            <circle
              cx="50%"
              cy="50%"
              r="130"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeOpacity="0.3"
            />
            <circle
              cx="50%"
              cy="50%"
              r="130"
              fill="none"
              stroke="white"
              strokeWidth="4"
              strokeDasharray={`${2 * Math.PI * 130}`}
              strokeDashoffset={`${2 * Math.PI * 130 * (1 - progress)}`}
              strokeLinecap="round"
              className="transition-all duration-300"
            />
          </svg>
        )}
      </motion.div>
    </div>
  );
}